/**
 * 同步事件总线与可观察值。
 *
 * 这是运行时的公共骨架：车辆状态机、告警、参数、指令、任务管理器都建立在它之上。
 *
 * 两条刻意的设计：
 *
 * 1. **同步派发。** 协议帧必须在同一次调用栈里走完"更新状态 → 通知订阅者"。
 *    若改成微任务派发，"读到最新状态"和"收到通知"之间就出现一个谁也没定义的窗口 ——
 *    在遥控场景里，这个窗口就是"界面显示的姿态比实际慢一拍"，
 *    而它无法通过任何单点测试发现，只能靠不给它存在的机会。
 *
 * 2. **监听器抛错必须被隔离。** 一个界面订阅者写错一行，不能让后面的遥测处理停摆。
 *    "因为某个面板崩了，所以参数下载中断"是不可接受的耦合。
 */

export type Unsubscribe = () => void;

/** `once` 之外的订阅形式都在这里；`emit` 是同步的。 */
export interface EventBus<EventMap extends Record<string, unknown>> {
  on<K extends keyof EventMap>(type: K, listener: (event: EventMap[K]) => void): Unsubscribe;
  once<K extends keyof EventMap>(type: K, listener: (event: EventMap[K]) => void): Unsubscribe;
  /** 同步派发给当前所有订阅者 */
  emit<K extends keyof EventMap>(type: K, event: EventMap[K]): void;
  clear(): void;
  /**
   * 当前订阅者数量。
   *
   * 存在的理由是**泄漏检查**：订阅忘记取消时，表现是"时间越长越卡"，
   * 而不是任何一处报错。有这个方法，测试就能直接断言"销毁后归零"。
   */
  listenerCount(type?: keyof EventMap): number;
}

export interface EventBusOptions {
  /**
   * 监听器抛错的出口。默认静默 ——
   * 刻意不打印到控制台：遥测每秒几十帧，一个坏掉的订阅者会瞬间刷满日志、
   * 把真正有用的信息挤出去。需要看见时由调用方自己注入。
   */
  readonly onListenerError?: (error: unknown, type: string) => void;
}

export function createEventBus<EventMap extends Record<string, unknown>>(
  options: EventBusOptions = {},
): EventBus<EventMap> {
  const listeners = new Map<keyof EventMap, Set<(event: never) => void>>();

  /** 内部统一按"未知事件类型"存；对外由泛型收窄，转换只在这一处发生。 */
  type AnyListener = (event: never) => void;

  const on = <K extends keyof EventMap>(
    type: K,
    listener: (event: EventMap[K]) => void,
  ): Unsubscribe => {
    let set = listeners.get(type);
    if (set === undefined) {
      set = new Set();
      listeners.set(type, set);
    }
    set.add(listener as AnyListener);
    return () => {
      set.delete(listener as AnyListener);
      // 空集合要及时清掉，否则"订阅过又退订"的事件类型会一直留在 Map 里
      if (set.size === 0) listeners.delete(type);
    };
  };

  const emit = <K extends keyof EventMap>(type: K, event: EventMap[K]): void => {
    const set = listeners.get(type);
    if (set === undefined || set.size === 0) return;

    // 先快照再派发。订阅者在回调里退订或新订是常见写法，
    // 而 Set 允许在迭代过程中被修改 —— 直接遍历会漏发或重复发，
    // 且这种 bug 只在"某个面板恰好在这一帧挂载"时出现。
    const snapshot = [...set];
    for (const listener of snapshot) {
      try {
        (listener as (event: EventMap[K]) => void)(event);
      } catch (error) {
        options.onListenerError?.(error, String(type));
      }
    }
  };

  const once = <K extends keyof EventMap>(
    type: K,
    listener: (event: EventMap[K]) => void,
  ): Unsubscribe => {
    const off = on(type, (event) => {
      off();
      listener(event);
    });
    return off;
  };

  const listenerCount = (type?: keyof EventMap): number => {
    if (type !== undefined) return listeners.get(type)?.size ?? 0;
    let total = 0;
    for (const set of listeners.values()) total += set.size;
    return total;
  };

  return { on, once, emit, clear: () => listeners.clear(), listenerCount };
}

/**
 * 一个"当前值 + 订阅变化"的容器。
 *
 * 状态的读取方式必须是"随时取当前值"而不是"等下一个事件"：
 * 界面挂载的时刻和遥测到达的时刻没有任何关系，
 * 后者会让新打开的面板空着等下一次推送 —— 在 1 Hz 的低频消息上就是"打开白屏一秒"。
 */
export interface ObservableValue<T> {
  get(): T;
  /** 值未变化时**不通知**订阅者；相等判断可注入（例如按引用比较整个快照） */
  set(next: T): void;
  subscribe(listener: (value: T) => void): Unsubscribe;
}

export function createObservableValue<T>(
  initial: T,
  equals: (a: T, b: T) => boolean = Object.is,
): ObservableValue<T> {
  let current = initial;
  const listeners = new Set<(value: T) => void>();

  return {
    get: () => current,
    set: (next: T) => {
      if (equals(current, next)) return;
      current = next;
      for (const listener of [...listeners]) listener(current);
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
